import axios from 'axios';
import { t } from '../i18n';
import { IS_EUROPE, LANGUAGE } from '../config/region';

// The page's language (its URL prefix) on every request, so authored text comes back in the language
// the reader is looking at rather than whatever the account last stored. See backend utils/region.py.
if (IS_EUROPE) axios.defaults.headers.common['X-UI-Language'] = LANGUAGE;

// Server errors arrive in English; the key is the server's wording, the value is what the user sees.
const ERROR_TRANSLATIONS = {
  'User not found': t('Пользователь не найден'),
  'Username must be between 3 and 20 characters': t('Имя пользователя должно содержать от 3 до 20 символов'),
  'Username can only contain letters and numbers': t('Имя пользователя может содержать только буквы, цифры и нижнее подчёркивание'),
  'Username already registered': t('Имя пользователя уже зарегистрировано'),
  'Incorrect username or password': t('Неверное имя пользователя или пароль'),
  'Novel not found': t('Новелла не найдена'),
  'Invalid admin key': t('Неверный ключ администратора'),
  'Invalid verification code': t('Неверный код верификации'),
  'Only registered users can like novels. Please create an account to like this novel.': t('Только зарегистрированные пользователи могут лайкать новеллы. Пожалуйста, создайте аккаунт, чтобы лайкать эту новеллу.'),
  /* Not localized on purpose: Building.js and BuildingNovelDetail.js detect the locked-creator case
     with err.message.includes('Доступ к конструктору закрыт'), so translating it here would break
     their access-denied screens. */
  'Creator access denied': 'Доступ к конструктору закрыт. Чтобы получить доступ, напишите @novelladmin в Telegram лично.'
};

const translateErrorMessage = (message) => {
  if (ERROR_TRANSLATIONS[message]) return ERROR_TRANSLATIONS[message];
  for (const [english, russian] of Object.entries(ERROR_TRANSLATIONS)) {
    if (message.includes(english)) return message.replace(english, russian);
  }
  return message;
};

// Toast notification system with adjustable dimensions
let toastContainer = null;
let toastConfig = {
  minWidth: '300px',
  maxWidth: '500px',
  minHeight: '40px',
  fontSize: '12px',
  padding: '8px 12px',
  position: 'top-center' // top-left, top-center, top-right
};

// Function to update toast configuration
export const setToastConfig = (config) => {
  toastConfig = { ...toastConfig, ...config };
};

const createToastContainer = () => {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'toast-container';
    
    let positionStyle = '';
    switch(toastConfig.position) {
      case 'top-left':
        positionStyle = 'top: 20px; left: 20px;';
        break;
      case 'top-right':
        positionStyle = 'top: 20px; right: 20px;';
        break;
      default: // top-center
        positionStyle = 'top: 20px; left: 50%; transform: translateX(-50%);';
    }
    
    toastContainer.style.cssText = `
      position: fixed;
      ${positionStyle}
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 6px;
      pointer-events: none;
    `;
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
};

const showToast = (message, type = 'error', duration = 4000) => {
  const container = createToastContainer();

  const toast = document.createElement('div');

  const colors = {
    error: { bg: 'rgba(40, 8, 8, 0.95)', border: 'rgba(255, 80, 80, 0.4)', text: 'rgba(255, 120, 120, 0.95)', icon: 'rgba(255, 80, 80, 0.8)' },
    success: { bg: 'rgba(8, 40, 16, 0.95)', border: 'rgba(80, 255, 120, 0.4)', text: 'rgba(120, 255, 160, 0.95)', icon: 'rgba(80, 255, 120, 0.8)' },
    info: { bg: 'rgba(8, 8, 40, 0.95)', border: 'rgba(123, 47, 255, 0.4)', text: 'rgba(200, 180, 255, 0.95)', icon: 'rgba(123, 47, 255, 0.8)' }
  };

  const color = colors[type] || colors.error;
  const icon = type === 'error' ? '!' : type === 'success' ? '✓' : 'i';

  toast.style.cssText = `
    background: ${color.bg};
    color: ${color.text};
    min-width: ${toastConfig.minWidth};
    max-width: ${toastConfig.maxWidth};
    min-height: ${toastConfig.minHeight};
    padding: ${toastConfig.padding};
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
    pointer-events: auto;
    transform: translateY(-100%);
    opacity: 0;
    transition: transform 0.3s ease, opacity 0.3s ease;
    border: 1px solid ${color.border};
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: ${toastConfig.fontSize};
    font-family: 'Raleway', sans-serif;
    font-weight: 500;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  `;

  toast.innerHTML = `
    <span style="flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; background: ${color.icon}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700;">${icon}</span>
    <span style="flex: 1; line-height: 1.4; word-break: break-word;">${message}</span>
    <button onclick="this.parentElement.style.opacity='0';this.parentElement.style.transform='translateY(-100%)';setTimeout(()=>this.parentElement.remove(),300)"
            style="background: rgba(255,255,255,0.06); border: 1px solid ${color.border}; border-radius: 6px; color: ${color.text}; cursor: pointer;
                   font-size: 16px; font-weight: 700;
                   padding: 0; margin: 0; line-height: 1;
                   width: 28px; height: 28px;
                   display: flex; align-items: center; justify-content: center;
                   flex-shrink: 0; -webkit-tap-highlight-color: transparent;">✕</button>
  `;
  
  container.appendChild(toast);
  
  // Animate in
  setTimeout(() => {
    toast.style.transform = 'translateY(0)';
    toast.style.opacity = '1';
  }, 10);

  // Auto remove
  setTimeout(() => {
    if (toast.parentNode) {
      toast.style.transform = 'translateY(-100%)';
      toast.style.opacity = '0';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.remove();
        }
      }, 300);
    }
  }, duration);
};

const formatErrorMessage = (err) => {
  let errorMessage = 'Unknown error';
  if (err.response?.data?.detail) {
    errorMessage = typeof err.response.data.detail === 'string'
      ? err.response.data.detail
      : JSON.stringify(err.response.data.detail);
  } else if (err.response?.data) {
    errorMessage = typeof err.response.data === 'string'
      ? err.response.data
      : JSON.stringify(err.response.data);
  } else if (err.message) {
    errorMessage = err.message;
  }

  return {
    status: err.response?.status || 'Unknown',
    message: errorMessage
  };
};

const isNetworkError = (err) => !err.response && !!err.request;

export const apiRequest = async (requestConfig, showSuccessToast = false, successMessage = '', suppressErrorToast = false) => {
  const MAX_RETRIES = 3;
  let lastErr;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await axios(requestConfig);
      if (showSuccessToast && successMessage) {
        showToast(successMessage, 'success');
      }
      return response;
    } catch (err) {
      lastErr = err;
      if (isNetworkError(err) && attempt < MAX_RETRIES) {
        continue;
      }
      break;
    }
  }

  const { message } = formatErrorMessage(lastErr);
  const translatedMessage = translateErrorMessage(message);
  const fullErrorMessage = t('Попробуйте снова или позже ({0})', translatedMessage);
  console.error('[API ERROR]', requestConfig.method, requestConfig.url, lastErr);
  if (!suppressErrorToast) showToast(fullErrorMessage, 'error');
  throw new Error(fullErrorMessage);
};

// Convenience methods
export const apiGet = (url, config = {}, showSuccessToast = false, successMessage = '', suppressErrorToast = false) => {
  return apiRequest({ method: 'GET', url, ...config }, showSuccessToast, successMessage, suppressErrorToast);
};

export const apiPost = (url, data = {}, config = {}, showSuccessToast = false, successMessage = '', suppressErrorToast = false) => {
  return apiRequest({ method: 'POST', url, data, ...config }, showSuccessToast, successMessage, suppressErrorToast);
};

export const apiPut = (url, data = {}, config = {}, showSuccessToast = false, successMessage = '') => {
  return apiRequest({ method: 'PUT', url, data, ...config }, showSuccessToast, successMessage);
};

export const apiDelete = (url, config = {}, showSuccessToast = false, successMessage = '') => {
  return apiRequest({ method: 'DELETE', url, ...config }, showSuccessToast, successMessage);
};

export { showToast };

